import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';

import { EventStatus, MapTypes } from '../../enums';
import { useEventRoute, useFleet, useHasAccess, useLocalStorage, useRouteEventTypes, useToggle } from '../../hooks';
import { Event, ParsedRouteDefinition } from '../../interfaces';
import { setKMLfilename, setKMLqueryData, useKMLFilename, useKMLQuery } from '../../queries';
import { EventsService, RouteService } from '../../services';
import {
  downloadTextFile,
  getEventCoords,
  getOriginalEventCoords,
  hash,
  isAggregableEvent,
  nothing,
} from '../../utils';
import { Button, InternalOnly, Relative } from '../atoms';
import { GoogleMap } from '../map/googleMap';
import { queryClient } from '../queryClient';
import { extractGoogleCoords, parseLocalKMLFile } from '../reviewGridMatchedEvents/KMLParser';
import { clearFromMap, drawKMLPolygonsAsPointsonMap, drawKMLonMap } from '../reviewGridMatchedEvents/KMLRenderer';
import { ButtonContainer, createRotatedIcon, HiddenFileInput } from './atoms';
import { SettingsModal } from './settingsModal';

interface Props {
  event: Event;
  states?: Record<number, EventStatus>;
  className?: string;
  style?: React.CSSProperties;
}

const icons = {
  green: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png',
  red: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
  gray: 'http://maps.google.com/mapfiles/ms/icons/ltblue-dot.png',
  bus: 'https://maps.gstatic.com/mapfiles/ms2/micons/bus.png',
  directed: '/directedLocation.png',
};

let StreetViewService: google.maps.StreetViewService;
let StreetViewSource: google.maps.StreetViewSource;

export const StreetView: FC<Props> = (props) => {
  const { event, states, style } = props;
  const { data: localKML } = useKMLQuery();
  const { data: localKMLFilename } = useKMLFilename();
  const subEvents = event?.sub_events ?? [];
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapType, setMapType] = useState<MapTypes>(MapTypes.Hybrid);
  const markers = useRef<google.maps.Marker[]>([]);
  const polylines = useRef<google.maps.Polyline[]>([]);
  const enforcementMarkersForSV = useRef<google.maps.Marker[]>([]);
  const [heading, setHeading] = useState<number>(0);
  const isAggregated = isAggregableEvent(event);
  const fleet = useFleet();
  const [manuallyLoadedFile, setManuallyLoadedFile] = useState(false);
  const [remoteKML, setRemoteKML] = useState<ParsedRouteDefinition | null>(null);
  const route = useEventRoute(event);
  const [settingsModal, toggleSettings] = useToggle(false);
  const [projectedLocation] = useLocalStorage('useProjectedLocation', false);
  const isInternalUser = useHasAccess();

  const getStreetView = () => map?.getStreetView();
  const panorama = getStreetView();
  const panoramaVisible = panorama?.getVisible();
  const [panoramaVisibleState, setPanoramaVisibleState] = useState(panoramaVisible);

  const [allRouteEventTypes] = useRouteEventTypes();
  const routeEventTypes = allRouteEventTypes.filter((et) => !et.includes('_MATCH'));

  const kml = localKML || remoteKML;
  const kmlFilename = localKMLFilename || event?.route_name;

  let averageLat = event?.lat;
  let averageLong = event?.lon;

  if (isAggregated) {
    averageLat = subEvents.reduce((acc, curr) => acc + curr.lat, 0) / subEvents.length;
    averageLong = subEvents.reduce((acc, curr) => acc + curr.lon, 0) / subEvents.length;
  }

  const center: google.maps.LatLngLiteral = {
    lat: averageLat,
    lng: averageLong,
  };

  useEffect(() => {
    const loadLibs = async () => {
      const library = (await google.maps.importLibrary('streetView')) as any;
      StreetViewService = library.StreetViewService;
      StreetViewSource = library.StreetViewSource;
    };
    loadLibs();
  }, []);

  const clearAllFromMap = () => {
    clearFromMap(polylines.current, map);
    clearFromMap(enforcementMarkersForSV.current, map);
  };

  const getIconImage = (event: Event): string => {
    if (!states) {
      if (event.status === EventStatus.approved) {
        return icons.green;
      }
      if (event.status === EventStatus.rejected) {
        return icons.red;
      }
      return icons.gray;
    }

    if (states[event.id] === EventStatus.approved) {
      return icons.green;
    }
    if (states[event.id] === EventStatus.rejected) {
      return icons.red;
    }
    return icons.gray;
  };

  const addEventMarkersToMap = () => {
    // Remove all markers (prevent memory leak and duplicate markers)
    markers?.current?.forEach((marker) => {
      marker.setMap(null);
      marker = null;
    });

    let newMarkers: google.maps.Marker[] = [];
    const getLocationFunc = projectedLocation ? getEventCoords : getOriginalEventCoords;

    if (isAggregated) {
      // Now add new markers
      newMarkers = subEvents.map((subEvent, idx) => {
        const coords = getLocationFunc(subEvent);
        return new google.maps.Marker({
          position: { lat: coords.lat, lng: coords.lon },
          map,
          icon: getIconImage(subEvent),
          title: idx.toString(),
        });
      });
    } else {
      const coords = getLocationFunc(event);
      newMarkers = [
        new google.maps.Marker({
          position: { lat: coords.lat, lng: coords.lon },
          map,
          icon: getIconImage(event),
          title: event.id.toString(),
        }),
      ];
    }

    if (projectedLocation) {
      const egoCoords = getOriginalEventCoords(event);
      const eventHeading = event?.metadata?.heading || event?.metadata?.route_metadata?.heading || 0;
      const mapHeading = map?.getHeading() || 0;
      newMarkers.push(
        new google.maps.Marker({
          position: { lat: egoCoords.lat, lng: egoCoords.lon },
          map,
          icon: createRotatedIcon(eventHeading, mapHeading),
          title: 'Ego location',
        }),
      );
    }

    markers.current = newMarkers;
  };

  const getAvgHeading = async () => {
    if (!isAggregated) {
      const heading = event?.metadata?.heading || event?.metadata?.route_metadata?.heading || 0;
      setHeading(heading);
      return heading;
    }

    const allMetadataPromise = subEvents.map((se) => EventsService.getEventMetadata(se.id));
    const allMetadata = await Promise.all(allMetadataPromise);
    const metadataWithHeadings = allMetadata.filter((m) => m.heading);
    const countOfHeadings = metadataWithHeadings.length;
    const avgHeading = countOfHeadings
      ? metadataWithHeadings.reduce((acc, curr) => acc + curr.heading, 0) / countOfHeadings
      : 0;
    setHeading(avgHeading);
    return avgHeading;
  };

  const setupStreetView = async (shouldMove = false) => {
    const newHeading = await getAvgHeading();

    const svs = new StreetViewService();
    const newPanorama = getStreetView();
    const streetViewRequest: google.maps.StreetViewResponse = await svs.getPanorama({
      location: center,
      radius: 50,
      sources: [StreetViewSource.OUTDOOR],
    });

    if (newPanorama) {
      // Ensure panorama is properly initialized with correct position if not using pegman
      if (shouldMove) {
        newPanorama.setPosition(streetViewRequest?.data?.location?.latLng ?? center);
      }

      newPanorama.setPov({
        heading: newHeading ?? 0,
        pitch: -10,
      });
    }
  };

  const handleToggleSV = () => {
    if (!panoramaVisible) {
      // manually enabling street view
      setupStreetView(true);
    }
    // https://issuetracker.google.com/issues/35830290?pli=1 thats why we need setzoom
    getStreetView()?.setZoom(1);
    getStreetView()?.setVisible(!panoramaVisible);
    setPanoramaVisibleState(!panoramaVisible);
  };

  const handleKMLClick = () => {
    document.getElementById('hiddenFileInput')?.click();
    return false;
  };

  const handleMapTypeClick = () => {
    if (mapType === MapTypes.Hybrid) {
      setMapType(MapTypes.Roadmap);
    } else {
      setMapType(MapTypes.Hybrid);
    }
  };

  const fileChanged = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    setManuallyLoadedFile(true);
    setRemoteKML(null);
    setKMLfilename(file.name);
    const results = await parseLocalKMLFile(file);
    setKMLqueryData(results);
  };

  const drawOverlays = () => {
    clearAllFromMap();
    const isSVvisible = getStreetView()?.getVisible();
    if (!map || !kml) {
      return;
    }

    if (isSVvisible) {
      enforcementMarkersForSV.current = drawKMLPolygonsAsPointsonMap(map, kml, center, routeEventTypes);
    } else {
      polylines.current = drawKMLonMap(map, kml, routeEventTypes);
    }
  };

  useEffect(() => {
    if (map && event) {
      setupStreetView(false);
      addEventMarkersToMap();
      const streetView = map.getStreetView();

      // Add listener for when Street View is toggled externally (via pegman or google ui)
      const streetViewChangedListener = streetView?.addListener('visible_changed', () => {
        const isVisible = !!map.getStreetView().getVisible();
        setPanoramaVisibleState(isVisible);
        drawOverlays();
      });

      // Add listener for map heading changes to update marker rotation
      const headingChangedListener = map.addListener('heading_changed', () => {
        if (projectedLocation) {
          addEventMarkersToMap();
        }
      });

      return () => {
        google.maps.event.removeListener(streetViewChangedListener);
        google.maps.event.removeListener(headingChangedListener);
      };
    }
    return nothing;
  }, [map, kml, event?.id, setPanoramaVisibleState, hash(routeEventTypes), projectedLocation]);

  useEffect(() => {
    const streetView = getStreetView();
    streetView?.setPov({
      heading,
      pitch: -10,
    });
  }, [map, heading]);

  useEffect(() => {
    setupStreetView(true);
  }, [event?.id]);

  useEffect(() => {
    drawOverlays();
  }, [kml, map, panorama, event?.id, hash(routeEventTypes)]);

  useEffect(() => {
    if (event) {
      addEventMarkersToMap();
    }
  }, [states]);

  const loadRemoteRoutefile = async () => {
    try {
      const routeDefinition = await queryClient.ensureQueryData({
        queryKey: ['routes', 'definitions', route.id, fleet.id],
        queryFn: () => RouteService.loadRawRouteDefinition(route.id),
      });

      const parsedKML = extractGoogleCoords(routeDefinition);
      setRemoteKML(parsedKML);
    } catch (e) {
      setRemoteKML(null);
    }
  };

  const downloadRouteDefinition = (evt: React.MouseEvent<HTMLElement>) => {
    evt.stopPropagation();
    evt.preventDefault();

    if (event?.route_id) {
      RouteService.loadRawRouteDefinition(event.route_id).then((res) => {
        downloadTextFile(res, kmlFilename + '.kml' || 'route.kml');
      });
    }

    return false;
  };

  useEffect(() => {
    if (event?.route_id && !manuallyLoadedFile) {
      loadRemoteRoutefile();
    }
  }, [event?.route_id, fleet?.id]);

  if (!event) {
    return null;
  }

  return (
    <Relative className={props.className} style={{ display: 'inline-block', ...style }}>
      <GoogleMap
        onMapLoad={setMap}
        center={center}
        zoom={20}
        heading={heading}
        tilt={isInternalUser ? 0 : undefined}
        style={{ flexGrow: '1', aspectRatio: '16/9', width: '100%' }}
        mapTypeId={mapType}
        mapTypeControl={false}
        streetView={panorama}
        mapId="streetview"
        controlSize={24}
      />

      <InternalOnly>
        <ButtonContainer topLeft>
          <Button size="sm" variant={'secondary'} onClick={toggleSettings}>
            ⛭
          </Button>
        </ButtonContainer>
      </InternalOnly>

      <ButtonContainer>
        <Button size="sm" variant={kml ? 'primary' : 'secondary'} onClick={handleKMLClick}>
          {kmlFilename || 'Load KML'}
          <InternalOnly>{!localKMLFilename && <span onClick={downloadRouteDefinition}>↓</span>}</InternalOnly>
        </Button>

        <Button
          size="sm"
          variant={panoramaVisibleState ? 'primary' : 'secondary'}
          onClick={handleToggleSV}
          data-accesskey="KeyM"
        >
          SV
        </Button>

        {!panoramaVisible && (
          <Button size="sm" variant={mapType === 'hybrid' ? 'primary' : 'secondary'} onClick={handleMapTypeClick}>
            Satellite
          </Button>
        )}

        <HiddenFileInput type="file" id="hiddenFileInput" onChange={fileChanged} />
      </ButtonContainer>

      {settingsModal && <SettingsModal toggleSettings={toggleSettings} />}
    </Relative>
  );
};
